import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBranch } from '@/features/branch/branch-context';
import { api, type PageMeta } from '@/lib/api';
import { withoutDemoClock } from './demo-clock';

/** Register engine (API /registers): kinds carry their own fields, statuses and actions. */
export type FieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'money'
  | 'date'
  | 'datetime'
  | 'select'
  | 'multiselect'
  | 'boolean'
  | 'staff';

export interface RegisterField {
  key: string;
  label: string;
  type: FieldType;
  required?: boolean;
  options?: { value: string; label: string }[];
  min?: number;
  max?: number;
  maxLength?: number;
  hint?: string;
  column?: boolean;
}

export type StatusTone = 'neutral' | 'info' | 'warning' | 'success' | 'danger';

export interface RegisterAction {
  key: string;
  label: string;
  from: string[];
  to: string | null;
  fields: RegisterField[];
  noteRequired: boolean;
  primary: boolean;
  danger: boolean;
  allowed: boolean;
}

export interface RegisterKind {
  kind: string;
  label: string;
  noun: string;
  area: string;
  rule: string | null;
  patient: 'none' | 'optional' | 'required';
  branchScoped: boolean;
  fields: RegisterField[];
  statuses: { value: string; label: string; tone: StatusTone }[];
  initialStatus: string;
  canWrite: boolean;
  /** RL-1820: the caller sees only the records they created. */
  onlyOwn?: boolean;
  actions: RegisterAction[];
}

export interface Named {
  id: string;
  fullName: string;
}

export interface RegisterRecord {
  id: string;
  kind: string;
  number: string | null;
  title: string;
  status: string;
  statusLabel: string;
  statusTone: StatusTone;
  data: Record<string, unknown>;
  dueAt: string | null;
  overdue: boolean;
  patient: Named | null;
  branch: { id: string; name: string } | null;
  owner: Named | null;
  createdAt: string;
  createdBy: Named | null;
  closedAt: string | null;
  /** RL-1826: archived past its retention period (out of the live list). */
  archivedAt?: string | null;
  version: number;
}

export interface RegisterEvent {
  id: string;
  action: string;
  label: string;
  fromStatus: string | null;
  toStatus: string | null;
  data: Record<string, unknown> | null;
  note: string | null;
  at: string;
  by: Named | null;
}

export interface RegisterRecordDetail extends RegisterRecord {
  events: RegisterEvent[];
}

/** GET /dispensary/registers/poison: the prescription book (one supply each). */
export interface PoisonBookEntry {
  id: string;
  at: string;
  rxNumber: string | null;
  medicine: string;
  group: string | null;
  qty: number;
  batch: string | null;
  patient: string;
  identity: string | null;
  address: string | null;
  prescriber: string;
  mmc: string | null;
}

/** RL-1634: one entry of the controlled-drug register (never edited). */
export interface ControlledDrugRegisterEntry {
  id: string;
  entryNo: number;
  medicine: { id: string; name: string };
  kind:
    | 'opening'
    | 'supply'
    | 'receipt'
    | 'transfer_in'
    | 'transfer_out'
    | 'return_supplier'
    | 'write_off'
    | 'count_adjust'
    | 'correction';
  suppliedAt: string;
  rxNumber: string | null;
  patientName: string | null;
  /** The patient's ID as on the record when written (snapshot). */
  patientIdentity: string | null;
  patientAddress?: string | null;
  prescriberName: string | null;
  prescriberMmc: string | null;
  prescriberAddress?: string | null;
  /** Who collected (RL-1630) and the last 4 of their ID. */
  collector?: 'patient' | 'other' | null;
  collectorName?: string | null;
  collectorIdLast4?: string | null;
  qtyIn: number;
  qtyOut: number;
  balanceAfter: number;
  batch: string | null;
  witnessName: string | null;
  recordedBy: string | null;
  reason: string | null;
  corrects: { id: string; entryNo: number } | null;
  correctedBy: { id: string; entryNo: number }[];
}

export type ControlledDrugRegisterKind = 'poison' | 'controlled';

/** RL-1634 review: register balance vs stock on hand, per medicine. */
export interface ControlledDrugSummary {
  medicine: { id: string; name: string };
  lastEntryNo: number | null;
  registerBalance: number | null;
  onHand: number;
  /** On hand minus the register balance; 0 = they agree. */
  discrepancy: number;
  inRegister: boolean;
}

export interface ControlledDrugRegisterPage {
  entries: ControlledDrugRegisterEntry[];
  medicines: ControlledDrugSummary[];
}

/** RL-1634: POST /dispensary/registers/{entryId}/corrections. */
export interface RegisterCorrectionBody {
  quantity: number;
  reason: string;
  /** RL-1942: required; a dispenser or doctor of the branch, not you. */
  witnessId: string;
}

/** Who can witness at the branch (dispensers and doctors, the caller left out). */
export interface WitnessCandidate {
  id: string;
  fullName: string;
  kind: 'dispenser' | 'doctor';
  signedIn: boolean;
}

export function useRegisterKinds() {
  return useQuery({
    queryKey: ['register-kinds'],
    queryFn: ({ signal }) =>
      api.get<RegisterKind[]>('/registers', { signal }).then((r) => r.data),
    staleTime: 5 * 60_000,
  });
}

/** Branch-scoped prescription book and controlled-drug register (RL-1634). */
export function usePoisonBook(enabled: boolean) {
  const { selected } = useBranch();
  return useQuery({
    queryKey: ['dispensary-register', 'poison', selected?.id ?? 'all'],
    queryFn: ({ signal }) =>
      api
        .get<PoisonBookEntry[]>('/dispensary/registers/poison', { signal })
        .then((r) => r.data),
    enabled: enabled && !!selected,
  });
}

/** RL-1634 review: one page (newest first) and the per-medicine balances. */
export function useControlledDrugRegister(enabled: boolean, page = 1) {
  const { selected } = useBranch();
  return useQuery({
    queryKey: [
      'dispensary-register',
      'controlled',
      selected?.id ?? 'all',
      page,
    ],
    queryFn: ({ signal }) =>
      api
        .get<ControlledDrugRegisterPage, PageMeta>(
          '/dispensary/registers/controlled',
          { signal, query: { page, pageSize: 50 } },
        )
        .then((r) => ({ ...r.data, meta: r.meta })),
    enabled: enabled && !!selected,
  });
}

/** RL-1635: "Correct entry": a new linked entry, never an edit. */
export function useCorrectRegisterEntry() {
  const qc = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async ({
      entryId,
      body,
    }: {
      entryId: string;
      body: RegisterCorrectionBody;
    }) =>
      (
        await api.post<{
          original: ControlledDrugRegisterEntry;
          correction: ControlledDrugRegisterEntry;
        }>(`/dispensary/registers/${entryId}/corrections`, body)
      ).data,
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ['dispensary-register'] }),
  });
}

/**
 * Witness candidates (RL-1942): dispensers and doctors with a role at the
 * branch, you left out (GET /dispensary/second-checkers?branchId=).
 */
export function useWitnessCandidates(enabled: boolean) {
  const { selected } = useBranch();
  return useQuery({
    queryKey: ['dispensary-witnesses', selected?.id ?? 'all'],
    queryFn: ({ signal }) =>
      api
        .get<WitnessCandidate[]>('/dispensary/second-checkers', {
          signal,
          query: { branchId: selected!.id },
        })
        .then((r) => r.data),
    enabled: enabled && !!selected,
    staleTime: 30_000,
  });
}

/** `archived`: RL-1826, the records archived past their period instead. */
export function useRegisterRecords(
  kind: RegisterKind | undefined,
  archived = false,
) {
  const { selected } = useBranch();
  const needsBranch = !!kind?.branchScoped && !selected;
  return {
    needsBranch,
    ...useQuery({
      queryKey: [
        'register-records',
        kind?.kind,
        selected?.id ?? 'all',
        ...(archived ? ['archived'] : []),
      ],
      queryFn: ({ signal }) =>
        api
          .get<RegisterRecord[]>(`/registers/${kind!.kind}`, {
            signal,
            query: archived ? { archived: true } : undefined,
          })
          .then((r) => r.data.map((x) => withoutDemoClock(x))),
      enabled: !!kind && !needsBranch,
    }),
  };
}

/** One record with its timeline (shared by the drawer and RL-2018's reload). */
export function registerRecordQuery(kind: string, id: string | null) {
  return {
    queryKey: ['register-record', kind, id],
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      api
        .get<RegisterRecordDetail>(`/registers/${kind}/${id}`, { signal })
        .then((r) => withoutDemoClock(r.data)),
  };
}

export function useRegisterRecord(kind: string, id: string | null) {
  return useQuery({ ...registerRecordQuery(kind, id), enabled: !!id });
}

export function useRegisterActions(kind: string) {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['register-records', kind] });
    void qc.invalidateQueries({ queryKey: ['register-record', kind] });
  };
  return {
    create: useMutation({
      mutationFn: (v: {
        data: Record<string, unknown>;
        patientId?: string;
        ownerStaffId?: string;
      }) =>
        api
          .post<RegisterRecordDetail>(`/registers/${kind}`, v)
          .then((r) => r.data),
      onSuccess: refresh,
    }),
    /**
     * RL-2018: PATCH /registers/:kind/:id corrects fields with a reason
     * (the register's write permission); the timeline keeps before and after.
     */
    correct: useMutation({
      mutationFn: (v: {
        id: string;
        data: Record<string, unknown>;
        reason: string;
        version: number;
      }) =>
        api
          .patch<RegisterRecordDetail>(`/registers/${kind}/${v.id}`, {
            data: v.data,
            reason: v.reason,
            version: v.version,
          })
          .then((r) => r.data),
      onSuccess: refresh,
    }),
    /** RL-1826: POST /registers/:kind/:id/unarchive (data_security:manage). */
    unarchive: useMutation({
      mutationFn: (v: { id: string; reason: string }) =>
        api
          .post<RegisterRecordDetail>(`/registers/${kind}/${v.id}/unarchive`, {
            reason: v.reason,
          })
          .then((r) => r.data),
      onSuccess: refresh,
    }),
    act: useMutation({
      mutationFn: (v: {
        id: string;
        action: string;
        data?: Record<string, unknown>;
        note?: string;
        version: number;
      }) =>
        api
          .post<RegisterRecordDetail>(
            `/registers/${kind}/${v.id}/actions/${v.action}`,
            { data: v.data, note: v.note, version: v.version },
          )
          .then((r) => r.data),
      onSuccess: refresh,
    }),
  };
}
